import { useState } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, FolderPlus, FolderOpen, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useProjects } from "@/hooks/useProjects";

interface ConvertEstimateDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  estimate: {
    id: string;
    project_id?: string | null;
    is_change_order?: boolean;
    customer_id?: string | null;
    customer_name?: string | null;
    total_amount?: number | null;
    line_items?: any[];
    estimate_number?: string | null;
  } | null;
  onNewProject?: () => void;
}

export const ConvertEstimateDialog = ({
  open,
  onOpenChange,
  estimate,
  onNewProject,
}: ConvertEstimateDialogProps) => {
  const navigate = useTabNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<
    "invoice" | "project" | "existing" | null
  >(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const { data: projects } = useProjects();

  if (!estimate) return null;

  const hasProject = !!estimate.project_id;
  const isCO = !!estimate.is_change_order;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["estimates"] });
    queryClient.invalidateQueries({ queryKey: ["estimate_detail"] });
    queryClient.invalidateQueries({ queryKey: ["contracts"] });
    queryClient.invalidateQueries({ queryKey: ["project_financials"] });
    invalidateFinancials(queryClient, { projectId: estimate?.project_id ?? null });
  };

  const handleConvertToInvoice = async () => {
    setLoading("invoice");
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Build lines first so we can compute accurate total
      const mappedLines = (estimate.line_items ?? []).flatMap(
        (item: any, idx: number) => {
          const subs = item.sub_lines ?? [];
          if (subs.length > 0) {
            return subs.map((sl: any, si: number) => ({
              description: sl.description || `Line ${idx + 1}.${si + 1}`,
              quantity: sl.qty ?? 1,
              unit_price: sl.unit_price ?? 0,
              line_total:
                sl.total_billed ??
                sl.total ??
                (sl.qty ?? 1) * (sl.unit_price ?? 0),
            }));
          }
          const qty = item.qty ?? item.Qty ?? 1;
          const billed = item.total_billed ?? item.total ?? 0;
          return [
            {
              description:
                item.description || item.Description || `Line ${idx + 1}`,
              quantity: qty,
              unit_price:
                item.unit_price ?? (qty > 0 ? billed / qty : 0),
              line_total: billed,
            },
          ];
        }
      );

      const invoiceTotal = mappedLines.reduce(
        (s, l) => s + (l.line_total || 0),
        0
      );

      const { data: invoice, error: invErr } = await supabase
        .from("invoices")
        .insert({
          company_id: companyId,
          invoice_number: null,
          invoice_date: new Date().toISOString().slice(0, 10),
          status: "approved" as any,
          approved_at: new Date().toISOString(),
          total: invoiceTotal,
          subtotal: invoiceTotal,
          source_estimate_id: estimate.id,
          customer_id: estimate.customer_id ?? null,
        } as any)
        .select("id")
        .single();
      if (invErr) throw invErr;

      if (mappedLines.length > 0) {
        const dbLines = mappedLines.map((l) => ({
          invoice_id: invoice.id,
          company_id: companyId,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          line_total: l.line_total,
        }));
        const { error: lineErr } = await supabase
          .from("invoice_lines")
          .insert(dbLines as any);
        if (lineErr) throw lineErr;
      }

      await supabase
        .from("estimates")
        .update({ status: "Closed" })
        .eq("id", estimate.id);

      invalidateAll();
      toast.success("Estimate converted to invoice");
      onOpenChange(false);
      navigate("/controller/invoices");
    } catch (err: any) {
      toast.error(err.message || "Failed to convert to invoice");
    } finally {
      setLoading(null);
    }
  };

  const handleAddToProject = async () => {
    if (!estimate.project_id) return;
    setLoading("project");
    try {
      const { data, error } = await supabase.rpc(
        "convert_estimate_to_project_sov",
        {
          p_estimate_id: estimate.id,
          p_project_id: estimate.project_id,
        } as any
      );
      if (error) throw error;
      const result = data as any;
      if (result?.error) throw new Error(result.error);

      invalidateAll();
      const action =
        result?.action === "first_contract"
          ? "Initial contract created"
          : "Change order SOV lines added";
      toast.success(action);
      onOpenChange(false);
      navigate(`/controller/projects/${estimate.project_id}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to add to project");
    } finally {
      setLoading(null);
    }
  };

  const handleAddToExistingProject = async () => {
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }
    setLoading("existing");
    try {
      const { data, error } = await supabase.rpc(
        "convert_estimate_to_project_sov",
        {
          p_estimate_id: estimate.id,
          p_project_id: selectedProjectId,
        } as any
      );
      if (error) throw error;
      const result = data as any;
      if (result?.error) throw new Error(result.error);

      invalidateAll();
      const action =
        result?.action === "first_contract"
          ? "Initial contract created"
          : "Change order SOV lines added";
      toast.success(action);
      onOpenChange(false);
      navigate(`/controller/projects/${selectedProjectId}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to add to project");
    } finally {
      setLoading(null);
    }
  };

  const handleNewProject = () => {
    onOpenChange(false);
    onNewProject?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Convert Estimate {estimate.estimate_number}</DialogTitle>
          <DialogDescription>
            Choose how to convert this accepted estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 mt-2">
          {/* Option 1: Standalone Invoice */}
          <Card
            className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md active:scale-[0.98]"
            onClick={!loading ? handleConvertToInvoice : undefined}
          >
            <CardContent className="p-4 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">
                  Convert to Invoice (Standalone)
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Create a standalone invoice from this estimate with no project
                  association. Line items will be mapped directly to invoice
                  lines.
                </p>
              </div>
              {loading === "invoice" && (
                <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-1" />
              )}
            </CardContent>
          </Card>

          {/* Option 2: Add to linked Project (CO-estimates only) */}
          {isCO && hasProject && (
            <Card
              className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md active:scale-[0.98]"
              onClick={!loading ? handleAddToProject : undefined}
            >
              <CardContent className="p-4 flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
                  <FolderPlus className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    Add to Project (Change Order)
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Add SOV lines to the linked project. If no contract exists
                    yet, this creates the initial contract. Otherwise, lines are
                    added as a change order at the front of the SOV.
                  </p>
                </div>
                {loading === "project" && (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-1" />
                )}
              </CardContent>
            </Card>
          )}

          {/* Option 2b: Convert to New Project (non-CO estimates) */}
          {!isCO && onNewProject && (
            <Card
              className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md active:scale-[0.98]"
              onClick={!loading ? handleNewProject : undefined}
            >
              <CardContent className="p-4 flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
                  <FolderPlus className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    Convert to New Project
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Create a new project with a contract and SOV populated from
                    this estimate's line items.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Option 3: Add to Existing Project (non-CO estimates) */}
          {!isCO && (
            <Card className="transition-all hover:border-primary/50 hover:shadow-md">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
                    <FolderOpen className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">
                      Add to Existing Project
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Select a project to add SOV lines to. Creates the first
                      contract if none exists, or adds as a change order if SOV
                      lines already exist.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedProjectId}
                    onValueChange={setSelectedProjectId}
                  >
                    <SelectTrigger className="flex-1 h-9">
                      <SelectValue placeholder="Select a project…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(projects ?? []).map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.project_number ? `${p.project_number} — ` : ""}
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    disabled={!selectedProjectId || !!loading}
                    onClick={handleAddToExistingProject}
                  >
                    {loading === "existing" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Add"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={!!loading}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
